import { API } from '../../services'
import { defineStore } from 'pinia'
import { Filters } from '../../types/Filters'
import { Pagination, defaultPagination } from '../../types/Pagination'
import { setPagination } from '../../services/utils/utils'
import { isOkStatus, isSuccessStatus } from '../../utils/http'
import { BusinessSaleConfig } from '../../types/BusinessSaleConfiguration'
import { toastMessage } from '../../utils/toastMessage'

// const { init } = useToast()

export interface Configuration {
  business_uuid: string
  business_name: string
  configurations: {
    [key: string]: any
  }
}

// const saleConfig: BusinessSaleConfig = {
//   business_id: 1,
//   track_sale_tax: false,
//   sale_percentage_tax: 0,
//   allow_sale_discounts: false,
//   offer_loyalty_points: false,
//   min_loyalty_points_required_to_redeem: 0,
//   currency_to_loyalty_points_unit_rate: 0,
//   loyalty_points_to_currency_unit_rate: 0,
//   track_staff_on_sale_form: false,
//   offer_staff_commission: false,
//   stage_of_offering_staff_commission: 1,
//   display_item_category_and_type_on_sale_form: false,
//   track_service_point_on_sale_form: false,
//   track_service_point_unit_on_sale_form: false,
//   allow_item_sale_comment: false,
//   allow_general_sale_comment: false,
//   default_payment_method: 5,
// }

export const useConfigurationStore = defineStore('configurationStore', {
  state: () => ({
    loading: false,
    currency: 'UGX',
    configs: [] as Configuration[],
    saleConfigs: {} as BusinessSaleConfig,
    /** Inventory group from the same endpoint; stock sale rules (e.g. allow_adding_out_of_stock_items_to_sale) live here on the API. */
    inventoryConfigs: {} as Record<string, unknown>,
    filters: {} as Filters,
    pagination: defaultPagination,
  }),

  actions: {
    initLoading() {
      this.loading = true
    },

    stopLoading() {
      this.loading = false
    },

    setConfigs(configs: Configuration[]) {
      this.configs = configs
    },

    setPagination(pagination: Pagination) {
      this.pagination = setPagination(pagination)
    },

    // CRUD Operations
    // async dispatchGet(filters?: Filters): Promise<any> {
    //   this.initLoading()
    //   const { status, data } = await API.configuration.getConfigs(filters, this.pagination).finally(this.stopLoading)

    //   if (isOkStatus(status)) {
    //     this.setConfigs(data.data)
    //     this.setPagination({
    //       meta: data.meta,
    //     })
    //   }

    //   return {
    //     success: isOkStatus(status),
    //     data: data.data,
    //   }
    // },

    // async dispatchUpdate(configId: number, config: Partial<Configuration>): Promise<any> {
    //   this.initLoading()
    //   const { status, data } = await API.configuration.updateConfig(configId, config).finally(this.stopLoading)

    //   if (isSuccessStatus(status)) {
    //     init({ message: 'Configuration updated successfully', color: 'success' })
    //     const index = this.configs.findIndex((c) => c.id === configId)
    //     if (index !== -1 && data.data) {
    //       this.configs[index] = { ...this.configs[index], ...data.data }
    //     }
    //   }

    //   return {
    //     success: isSuccessStatus(status),
    //     data: data.data,
    //   }
    // },

    // async dispatchGetById(configId: number): Promise<any> {
    //   this.initLoading()
    //   const { status, data } = await API.configuration.getConfigById(configId).finally(this.stopLoading)

    //   return {
    //     success: isOkStatus(status),
    //     data: data.data,
    //   }
    // },

    async dispatchGetBusinessSalesConfigurations(): Promise<any> {
      this.initLoading()
      try {
        const { status, data } = await API.configuration.getBusinessSalesConfigurations().finally(this.stopLoading)

        if (isOkStatus(status)) {
          const grouped = data?.data?.configurations ?? {}
          // Flat key → value map from this endpoint (sales UI expects primitives, not { value, field_type }).
          this.saleConfigs = grouped.sales || {}
          this.inventoryConfigs = grouped.inventory || {}
        }

        return {
          success: isOkStatus(status),
          data: data?.data,
        }
      } catch {
        this.stopLoading()
        return {
          success: false,
          data: undefined,
        }
      }
    },

    /** Sync inventory group into the store (supports flat or `{ value }` field-type shapes). */
    syncInventoryConfigsFromGrouped(grouped: Record<string, unknown> | undefined) {
      const inventory = grouped?.inventory
      this.inventoryConfigs = (inventory as Record<string, unknown>) || {}
    },

    async dispatchGetAllConfigurations(): Promise<any> {
      this.initLoading()
      const { status, data } = await API.configuration.getAllConfigurations().finally(this.stopLoading)

      if (isOkStatus(status)) {
        this.syncInventoryConfigsFromGrouped(data.data?.configurations)
      }

      return {
        success: isOkStatus(status),
        data: data.data,
      }
    },

    async dispatchUpdateConfiguration(configKey: string, value: any): Promise<any> {
      this.initLoading()
      try {
        const { status, data } = await API.configuration.updateConfiguration(configKey, value).finally(this.stopLoading)

        if (isSuccessStatus(status)) {
          toastMessage(true, 'Configuration updated successfully')
          this.syncInventoryConfigsFromGrouped(data.data?.configurations)
        } else {
          toastMessage(false, 'Failed to update configuration')
        }

        return {
          success: isSuccessStatus(status),
          data: data.data,
        }
      } catch (error: unknown) {
        this.stopLoading()
        // Interceptor already toasted; do not surface Axios status text here.
        return {
          success: false,
          error,
        }
      }
    },
  },
})
